import { useMutation, useQueryClient } from '@tanstack/react-query';
import BillingService from '../Services/billing/BillingService';
import { SUBSCRIPTION_STATUS_QUERY_KEY } from '../queries/useGetBilling';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const useBillingMutations = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const startFreeTrialMutation = useMutation({
    mutationFn: () => {
      return toast.promise(
        BillingService.startFreeTrial(),
        {
          loading: 'Activation de votre période d\'essai...',
          success: async (data) => {
            // Check if the response indicates success
            if (data && data.success) {
              // Invalidate subscription status queries
              queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_STATUS_QUERY_KEY] });
              queryClient.invalidateQueries({ queryKey: ['account'] });
              
              // Wait a bit for the queries to update
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Navigate to home page after success
              setTimeout(() => {
                navigate('/app/home', { replace: true });
              }, 2000);

              return data.message || 'Période d\'essai activée avec succès!';
            } else {
              // If success is false, throw an error to trigger the error handler
              throw new Error(data.message || 'Échec de l\'activation de la période d\'essai');
            }
          },
          error: (error) => {
            console.error('Failed to start free trial:', error);
            
            // Extract error message from the response
            let errorMessage = 'Erreur lors de l\'activation de la période d\'essai';
            
            if (error.response?.data) {
              const responseData = error.response.data;
              
              // Handle the standardized error response
              if (responseData.message) {
                errorMessage = responseData.message;
              }
            } 
            // Handle specific HTTP status codes
            else if (error.response?.status === 409) {
              errorMessage = 'Vous avez déjà utilisé votre période d\'essai gratuite';
            } else if (error.response?.status === 404) {
              errorMessage = 'Plan d\'essai gratuit non disponible';
            } else if (error.response?.status === 401) {
              errorMessage = 'Vous devez être connecté pour activer la période d\'essai';
            } else if (error.response?.status >= 500) {
              errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
            }
            
            return errorMessage;
          }
        }
      );
    }
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => {
      return toast.promise(
        BillingService.cancelSubscription(),
        {
          loading: 'Annulation de votre abonnement...',
          success: (data) => {
            // Check if the response indicates success
            if (data && data.success) {
              // Invalidate subscription status queries
              queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_STATUS_QUERY_KEY] });
              queryClient.invalidateQueries({ queryKey: ['account'] });
              
              return data.message || 'Abonnement annulé avec succès!';
            } else {
              // If success is false, throw an error to trigger the error handler
              throw new Error(data.message || 'Échec de l\'annulation de l\'abonnement');
            }
          },
          error: (error) => {
            console.error('Failed to cancel subscription:', error);
            
            // Extract error message from the response
            let errorMessage = 'Erreur lors de l\'annulation de l\'abonnement';
            
            if (error.response?.data) {
              const responseData = error.response.data;
              
              // Handle the standardized error response
              if (responseData.message) {
                errorMessage = responseData.message;
              }
              
              // Handle specific error codes
              if (responseData.code) {
                switch (responseData.code) {
                  case 'NO_ACTIVE_SUBSCRIPTION':
                    errorMessage = 'Aucun abonnement actif trouvé';
                    break;
                  case 'SUBSCRIPTION_ALREADY_CANCELED':
                    errorMessage = 'Cet abonnement est déjà annulé';
                    break;
                  case 'CANCELLATION_NOT_ALLOWED':
                    errorMessage = 'L\'annulation n\'est pas autorisée pour ce type d\'abonnement';
                    break;
                  default:
                    errorMessage = responseData.message || 'Erreur lors de l\'annulation';
                    break;
                }
              }
            } 
            // Handle HTTP status codes
            else if (error.response?.status === 404) {
              errorMessage = 'Aucun abonnement actif trouvé';
            } else if (error.response?.status === 403) {
              errorMessage = 'Vous n\'êtes pas autorisé à annuler cet abonnement';
            } else if (error.response?.status >= 500) {
              errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
            }
            
            return errorMessage;
          }
        }
      );
    }
  });

  const createSubscriptionMutation = useMutation({
    mutationFn: async ({ plan_id, has_plus, client_phone, passcode }) => {
      const serviceCall = BillingService.createSubscription({ 
        plan_id, 
        has_plus, 
        client_phone, 
        passcode,
        language: 'FR'
      });

      toast.promise(serviceCall, {
        loading: 'Traitement de votre abonnement...',
        success: (data) => {
          if (data?.success) {
            return data.message || 'Abonnement créé avec succès!';
          }
          throw new Error(data?.message || 'Échec de la création de l\'abonnement');
        },
        error: (error) => {
          console.error('Failed to create subscription:', error);
          
          let errorMessage = 'Erreur de paiement';
          
          if (error.response?.data) {
            const responseData = error.response.data;
            
            if (responseData.message) {
              errorMessage = responseData.message;
            }
            
            if (responseData.code) {
              switch (responseData.code) {
                case 'SUBSCRIPTION_EXISTS':
                  errorMessage = 'Vous avez déjà un abonnement actif';
                  break;
                case 'INSUFFICIENT_FUNDS':
                  errorMessage = 'Solde insuffisant pour effectuer cette transaction';
                  break;
                case 'INVALID_PASSCODE':
                  errorMessage = 'Code de paiement invalide. Vérifiez et réessayez';
                  break;
                case 'INVALID_PHONE':
                  errorMessage = 'Numéro de téléphone invalide. Vérifiez et réessayez';
                  break;
                case 'PAYMENT_SERVICE_ERROR':
                  errorMessage = 'Service de paiement temporairement indisponible. Veuillez réessayer';
                  break;
                case 'PAYMENT_PROCESSING_ERROR':
                  errorMessage = 'Erreur de traitement du paiement. Veuillez réessayer';
                  break;
                case 'PAYMENT_AUTH_EXPIRED':
                  errorMessage = 'Session de paiement expirée. Veuillez réessayer';
                  break;
                case 'CONNECTION_TIMEOUT':
                  errorMessage = 'Délai de connexion dépassé. Vérifiez votre connexion internet';
                  break;
                case 'NETWORK_ERROR':
                  errorMessage = 'Erreur réseau. Vérifiez votre connexion et réessayez';
                  break;
                case 'PAYMENT_VALIDATION_ERROR':
                  if (responseData.errors) {
                    const firstError = Object.values(responseData.errors)[0];
                    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
                  } else {
                    errorMessage = 'Informations de paiement invalides';
                  }
                  break;
                default:
                  errorMessage = responseData.message || responseData.details || 'Erreur lors du traitement du paiement';
                  break;
              }
            }
          } else if (error.response?.status === 409) {
            errorMessage = 'Vous avez déjà un abonnement actif';
          } else if (error.response?.status === 422) {
            errorMessage = 'Informations de paiement invalides. Vérifiez votre numéro de téléphone et code de paiement.';
          } else if (error.response?.status >= 500) {
            errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
          }
          
          return errorMessage;
        }
      });

      const data = await serviceCall;

      if (data?.success) {
        window.location.replace('/app/home');
        return data;
      }

      throw new Error(data?.message || 'Échec de la création de l\'abonnement');
    }
  });

  return {
    startFreeTrialMutation,
    cancelSubscriptionMutation,
    createSubscriptionMutation
  };
};
